import React, { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Label,
} from "recharts";

import styled from "styled-components";
import type { RealtimeParams } from "api/businessDashboard";
import type { RealtimeMemberMetric } from "types/BusinessDashboard";

import { useRealtimeMembersQuery } from "queries/Business/Dashboard/useDashboardQueries";

import { HelpIcon } from "assets/icons/system";
import { UserIcon as DashboardUserIcon } from "assets/icons/businessDashboard";
import { MEMBERS_PASS_TYPE_MAP } from "constants/businessDashboard";

import { CenterLabel, CustomTooltip } from "./ui-kit/chart";
import { MembersBreakdownCard } from "./ui-kit/members-breakdown-card";

import { RealtimeMembersSectionError } from "./members-section.error";
import { RealtimeMembersSectionSkeleton } from "./members-section.skeleton";
import { useSelector } from "react-redux";
import { BusinessDashboardHelpHoverCard } from "../ui-kit/help-modal";

dayjs.locale("ko");

const SectionWrapper = styled.div`
  padding: 24px;
  background-color: #f3f7ff;
  position: relative;
`;

const BackColor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 194px;
  background-color: #0f2859;
`;

const BottomBackColor = styled.div`
  background-color: #f3f7ff;
  position: absolute;
  left: 0;
  bottom: -50px;
  width: 100%;
  height: 194px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  margin-bottom: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  font-weight: bold;
  color: #fff;
`;

const SectionTime = styled.p`
  font-size: 12px;
  font-weight: bold;
  line-height: 14px;
  letter-spacing: -0.2px;
  color: #949db2;
  margin: 0;
`;

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const StyledCard = styled.div`
  padding: 0;
  border-radius: 16px;
  border: none;
  box-shadow: none;
  background: #fff;
  z-index: 10;
  position: relative;
`;

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 12px;
`;

const PieChartWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 396px;
  padding: 0 12px;
`;

const BreakdownCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 12px;
`;

export const RealtimeMembersSection = () => {
  const today = dayjs().format("YYYY-MM-DD");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: RealtimeParams = {
      referenceDate: today,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [today, partnerInfo]);

  const {
    data: membersData,
    isLoading: isMembersLoading,
    isError: isMembersError,
    refetch: refetchMembersData,
  } = useRealtimeMembersQuery(apiParams);

  const handleRetryRevenueData = () => {
    refetchMembersData();
  };

  if (isMembersLoading) return <RealtimeMembersSectionSkeleton />;
  if (isMembersError || !membersData)
    return <RealtimeMembersSectionError onRetry={handleRetryRevenueData} />;

  const { total, ...breakdownItems } = membersData;
  const totalMembersValue = total?.count ?? 0;
  const totalMembersChangeRate = total?.changeRate;

  const memberDataForUI = (() => {
    const validApiKeys = Object.keys(breakdownItems).filter(
      (key) => key !== "referenceDatetime"
    );

    return validApiKeys.map((apiKey) => {
      const uiInfo = Object.values(MEMBERS_PASS_TYPE_MAP).find(
        (mapItem) => mapItem.apiKey === apiKey
      );
      const metric = breakdownItems[
        apiKey as keyof typeof breakdownItems
      ] as RealtimeMemberMetric;

      return {
        name: uiInfo?.title || apiKey,
        value: metric.count,
        changeRate: metric.changeRate,
        color: uiInfo?.color || "#cccccc",
        lightColor: uiInfo?.lightColor || "#cccccc",
        bgColor: uiInfo?.bgColor || "#F3F4F6",
      };
    });
  })();

  return (
    <SectionWrapper>
      <BackColor />
      <BottomBackColor />
      <SectionHeader>
        <DashboardUserIcon />
        <HeaderContent>
          <SectionTitle>실시간 유효 회원</SectionTitle>
          <BusinessDashboardHelpHoverCard type="activeMembers">
            <HelpButton type="button">
              <HelpIcon />
            </HelpButton>
          </BusinessDashboardHelpHoverCard>
          <SectionTime>오늘 {dayjs().format("HH:mm")} 기준</SectionTime>
        </HeaderContent>
      </SectionHeader>
      <StyledCard>
        <StyledCardContent>
          <PieChartWrap>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={memberDataForUI}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={110}
                  dataKey="value"
                  paddingAngle={1.5}
                  isAnimationActive={false}
                >
                  {memberDataForUI.map((entry, index) => (
                    <Cell
                      key={`cell-${entry.name}-${index}`}
                      fill={entry.lightColor}
                    />
                  ))}
                  <Label
                    content={
                      <CenterLabel
                        totalValue={totalMembersValue}
                        changeRate={totalMembersChangeRate}
                      />
                    }
                    position="center"
                  />
                </Pie>
                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      totalMembers={totalMembersValue}
                    />
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </PieChartWrap>
          <BreakdownCardsGrid>
            {memberDataForUI.map((item) => (
              <MembersBreakdownCard
                key={item.name}
                title={item.name}
                value={item.value}
                changeRate={item.changeRate}
                color={item.color}
                bgColor={item.bgColor}
                percentage={
                  totalMembersValue > 0
                    ? Math.round((item.value / totalMembersValue) * 100)
                    : 0
                }
              />
            ))}
          </BreakdownCardsGrid>
        </StyledCardContent>
      </StyledCard>
    </SectionWrapper>
  );
};
